import { Loader2 } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import {
  Controller,
  type Control,
  type FieldErrors,
  type Path,
  type PathValue,
  type UseFormSetValue,
} from 'react-hook-form';

import { buscarCep } from '@/api/cep';
import { type EnderecoForm, mascararCep, SEM_UF, UFS } from '@/shared/lib/endereco';
import { Campo, CLASSE_CONTROLE } from '@/shared/components/Campo';
import { Input } from '@/shared/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/shared/ui/select';

interface Props<T extends { endereco: EnderecoForm }> {
  control: Control<T>;
  /** Para a busca do CEP preencher os outros campos. */
  setValue: UseFormSetValue<T>;
  erros: FieldErrors<T>['endereco'];
  /** Prefixo dos ids: o diálogo do tutor abre por cima do formulário do paciente. */
  idDe?: (campo: string) => string;
}

type SituacaoDoCep =
  | { tipo: 'parado' }
  | { tipo: 'buscando' }
  | { tipo: 'achou' }
  | { tipo: 'nao-achou' }
  | { tipo: 'falhou' };

const AVISO_DO_CEP: Partial<Record<SituacaoDoCep['tipo'], string>> = {
  buscando: 'Buscando endereço...',
  achou: 'Endereço preenchido pelo CEP. Confira e informe o número.',
  'nao-achou': 'CEP não encontrado. Preencha o endereço manualmente.',
  falhou: 'Não foi possível buscar o CEP agora. Preencha o endereço manualmente.',
};

/**
 * Bloco de endereço para formulários react-hook-form com um campo `endereco`.
 *
 * Usado no perfil (endereço da clínica) e no tutor — os dois saem no
 * receituário de controle especial.
 *
 * Ao completar os 8 dígitos do CEP, busca o endereço e preenche logradouro,
 * bairro, cidade e UF; número e complemento ficam com a pessoa (o CEP não os
 * tem). A busca só dispara por DIGITAÇÃO: abrir o formulário de quem já tem
 * CEP gravado não sobrescreve o endereço que ele ajustou à mão.
 */
export function CamposDeEndereco<T extends { endereco: EnderecoForm }>({
  control,
  setValue,
  erros,
  idDe = (campo) => `endereco-${campo}`,
}: Props<T>) {
  const nome = (campo: keyof EnderecoForm) => `endereco.${campo}` as Path<T>;
  const erro = (campo: keyof EnderecoForm) =>
    (erros as Partial<Record<keyof EnderecoForm, { message?: string }>> | undefined)?.[campo]?.message;

  const [situacao, setSituacao] = useState<SituacaoDoCep>({ tipo: 'parado' });
  const busca = useRef<AbortController | null>(null);

  // Fechar o formulário no meio da busca não pode preencher campo de um form que já saiu.
  useEffect(() => () => busca.current?.abort(), []);

  async function aoCompletarCep(cep: string) {
    busca.current?.abort();
    const controle = new AbortController();
    busca.current = controle;

    setSituacao({ tipo: 'buscando' });
    try {
      const achado = await buscarCep(cep, controle.signal);
      if (controle.signal.aborted) return;

      if (!achado) {
        setSituacao({ tipo: 'nao-achou' });
        return;
      }

      const preencher = (campo: keyof EnderecoForm, valor: string) =>
        setValue(nome(campo), valor as PathValue<T, Path<T>>, { shouldDirty: true, shouldValidate: true });

      // CEP geral de cidade pequena vem sem logradouro/bairro: não apaga o que
      // a pessoa já tinha digitado nesses campos.
      if (achado.logradouro) preencher('logradouro', achado.logradouro);
      if (achado.bairro) preencher('bairro', achado.bairro);
      preencher('cidade', achado.cidade);
      if ((UFS as readonly string[]).includes(achado.uf)) preencher('uf', achado.uf);

      setSituacao({ tipo: 'achou' });
      // O próximo campo que só a pessoa sabe.
      document.getElementById(idDe('numero'))?.focus();
    } catch {
      if (!controle.signal.aborted) setSituacao({ tipo: 'falhou' });
    }
  }

  const texto = (campo: keyof EnderecoForm, rotulo: string, extra?: { placeholder?: string }) => (
    <Campo id={idDe(campo)} rotulo={rotulo} erro={erro(campo)}>
      <Controller
        name={nome(campo)}
        control={control}
        render={({ field }) => (
          <Input
            id={idDe(campo)}
            className={CLASSE_CONTROLE}
            placeholder={extra?.placeholder}
            value={String(field.value ?? '')}
            onChange={(e) => field.onChange(e.target.value)}
            onBlur={field.onBlur}
          />
        )}
      />
    </Campo>
  );

  const aviso = AVISO_DO_CEP[situacao.tipo];

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Campo id={idDe('cep')} rotulo="CEP" erro={erro('cep')}>
          <Controller
            name={nome('cep')}
            control={control}
            render={({ field }) => (
              <div className="relative">
                <Input
                  id={idDe('cep')}
                  className={CLASSE_CONTROLE}
                  placeholder="00000-000"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  value={String(field.value ?? '')}
                  onChange={(e) => {
                    const anterior = String(field.value ?? '').replace(/\D/g, '');
                    const valor = mascararCep(e.target.value);
                    field.onChange(valor);

                    const digitos = valor.replace(/\D/g, '');
                    if (digitos.length === 8 && digitos !== anterior) void aoCompletarCep(digitos);
                    else if (digitos.length < 8) setSituacao({ tipo: 'parado' });
                  }}
                  onBlur={field.onBlur}
                />
                {situacao.tipo === 'buscando' && (
                  <Loader2
                    className="absolute right-2.5 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground"
                    aria-hidden
                  />
                )}
              </div>
            )}
          />
        </Campo>
        <div className="sm:col-span-2">{texto('logradouro', 'Logradouro', { placeholder: 'Rua, avenida...' })}</div>
      </div>

      {aviso && !erro('cep') && (
        <p
          role="status"
          className={
            situacao.tipo === 'nao-achou' || situacao.tipo === 'falhou'
              ? '-mt-2 text-xs text-amber-600 dark:text-amber-400'
              : '-mt-2 text-xs text-muted-foreground'
          }
        >
          {aviso}
        </p>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {texto('numero', 'Número')}
        <div className="sm:col-span-2">{texto('complemento', 'Complemento', { placeholder: 'Sala, bloco...' })}</div>
      </div>
      {texto('bairro', 'Bairro')}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="sm:col-span-2">{texto('cidade', 'Cidade')}</div>
        <Campo id={idDe('uf')} rotulo="UF" erro={erro('uf')}>
          <Controller
            name={nome('uf')}
            control={control}
            render={({ field }) => (
              <Select value={String(field.value)} onValueChange={field.onChange}>
                <SelectTrigger id={idDe('uf')} className={CLASSE_CONTROLE} aria-label="UF do endereço">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={SEM_UF}>—</SelectItem>
                  {UFS.map((uf) => (
                    <SelectItem key={uf} value={uf}>
                      {uf}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        </Campo>
      </div>
    </div>
  );
}
